import React, { memo, useState, useMemo } from "react";
import { Search, X } from "lucide-react";
import BookCard from "./BookCard";
import EmptyState from "./EmptyState";
import { StandardButton } from "./ui/standard-button";
import { Transmission } from "@/services/transmissionsService";
import { getOptimizedSettings } from "@/utils/performance";
import { ConceptualBridge } from "@/services/patternRecognition";

/** Below this many signals the whole collection fits on screen, so a search box
 *  would be clutter rather than help. */
const SEARCH_THRESHOLD = 8;

interface AIRecommendation {
  reason: string;
  cluster_connection: string;
}

interface TransmissionsListProps {
  transmissions: Transmission[];
  loading: boolean;
  onEdit: (book: Transmission) => void;
  onKeep: (book: Transmission) => void;
  onDiscard: (book: Transmission) => void;
  onAddNew: () => void;
  onAuthorClick?: (authorName: string) => void;
  getBookBridges?: (bookId: string) => ConceptualBridge[];
  aiRecommendations?: Map<string, AIRecommendation>;
}

const TransmissionsList = memo(({ 
  transmissions, 
  loading, 
  onEdit, 
  onKeep, 
  onDiscard, 
  onAddNew,
  onAuthorClick,
  getBookBridges,
  aiRecommendations
}: TransmissionsListProps) => {
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());
  const [optimisticTransmissions, setOptimisticTransmissions] = useState<Transmission[]>([]);
  const [query, setQuery] = useState("");

  // Get device-specific optimizations
  const optimizedSettings = useMemo(() => getOptimizedSettings(), []);

  // Title / author / tag match. Diacritics are folded so "Lem" finds "Stanisław
  // Lem" and "Zulawski" finds "Żuławski" — the catalogue is full of accented
  // author names and an exact-match search would hide them.
  const visibleTransmissions = useMemo(() => {
    const q = query.trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    if (!q) return optimisticTransmissions;
    const fold = (s?: string | null) =>
      (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    return optimisticTransmissions.filter((b) =>
      fold(b.title).includes(q) ||
      fold(b.author).includes(q) ||
      (b.tags || []).some((t) => fold(t).includes(q))
    );
  }, [optimisticTransmissions, query]);

  /**
   * The most recently added signal, so it can carry a thin edge and be findable
   * the moment you return from adding it.
   *
   * Derived from the UNFILTERED list by max(created_at) rather than taking the
   * first row: the fetch is already newest-first, but a search narrows what is
   * on screen, and "first visible result" would then move the marker onto
   * whatever happened to match the query.
   */
  const latestId = useMemo(() => {
    let newestId: number | null = null;
    let newestAt = -Infinity;
    for (const t of optimisticTransmissions) {
      const at = t.created_at ? new Date(t.created_at).getTime() : NaN;
      if (Number.isFinite(at) && at > newestAt) {
        newestAt = at;
        newestId = t.id;
      }
    }
    return newestId;
  }, [optimisticTransmissions]);

  // Keep optimistic list in sync with props
  React.useEffect(() => {
    setOptimisticTransmissions(transmissions);
  }, [transmissions]);

  const handleDiscard = async (book: Transmission) => {
    // Immediately remove from local state for instant feedback
    setOptimisticTransmissions(prev => prev.filter(t => t.id !== book.id));
    setDeletingIds(prev => new Set(prev).add(book.id));
    
    try {
      await onDiscard(book);
      // Clean up deletion state on success
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(book.id);
        return newSet;
      });
    } catch (error) {
      // Restore the book if deletion fails
      setOptimisticTransmissions(transmissions);
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(book.id);
        return newSet;
      });
    }
  };

  // Match Signal Archive layout: 3 columns max for proper breathing room
  const gridClasses = "grid gap-4 md:grid-cols-2 lg:grid-cols-3";

  if (loading && optimisticTransmissions.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full border-2 border-dashed border-slate-600 flex items-center justify-center">
          <div className={`w-6 h-6 rounded-full border-2 border-blue-400 ${optimizedSettings.reduceAnimations ? '' : 'animate-pulse'}`} />
        </div>
        <p className="text-slate-400">Loading transmissions...</p>
      </div>
    );
  }

  if (optimisticTransmissions.length === 0) {
    return (
      <EmptyState
        title="No signals yet"
        description="Begin mapping your intellectual journey through the books that shape you"
        action={
          <StandardButton
            onClick={onAddNew}
            variant="standard"
            className="touch-manipulation active:scale-95"
          >
            Log Your First Signal
          </StandardButton>
        }
      />
    );
  }

  return (
    <>
      {/* Search across the collection. Only appears once the library is big
          enough to need it, so small libraries keep the existing layout. */}
      {optimisticTransmissions.length >= SEARCH_THRESHOLD && (
        <div className="mb-4 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your transmissions by title, author or tag…"
            aria-label="Search transmissions"
            className="w-full h-9 pl-9 pr-9 rounded-lg bg-slate-900/40 border border-slate-700/60 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/40 transition-colors"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-slate-300 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {visibleTransmissions.length === 0 ? (
        <p className="text-slate-400 text-sm py-8 text-center">
          No transmissions match “{query}”.
        </p>
      ) : (
    <div className={gridClasses}>
      {visibleTransmissions.map(book => {
        const bridges = getBookBridges ? getBookBridges(book.id.toString()) : [];
        const aiRec = aiRecommendations?.get(book.title.toLowerCase()) || 
                      aiRecommendations?.get(book.id.toString());
        
        return (
          <div
            key={book.id}
            /* rounded-lg matches BookCard's own radius so the ring sits flush
               against the card edge rather than cutting its corners. */
            className={`transition-all ${optimizedSettings.reduceAnimations ? 'duration-150' : 'duration-300'} ${
              deletingIds.has(book.id) ? 'opacity-0 scale-95 pointer-events-none' : ''
            } ${
              book.id === latestId ? 'rounded-lg ring-1 ring-cyan-400/40' : ''
            }`}
          >
              <BookCard
                id={book.id}
                title={book.title}
                author={book.author}
                status={book.status}
                tags={book.tags}
                rating={book.rating}
                coverUrl={book.cover_url}
                publisher_series={book.publisher_series}
                isbn={book.isbn}
                apple_link={book.apple_link}
                is_favorite={book.is_favorite}
                aiRecommendation={aiRec}
                onEdit={() => onEdit(book)}
                onKeep={() => onKeep(book)}
                onDiscard={() => handleDiscard(book)}
                onAuthorClick={onAuthorClick}
                bridges={bridges}
                publicationYear={book.publication_year}
                protagonist={book.protagonist}
                protagonistPortraitUrl={book.protagonist_portrait_url}
                starRating={book.star_rating ?? null}
              />
          </div>
        );
      })}
    </div>
      )}
    </>
  );
});

TransmissionsList.displayName = 'TransmissionsList';

export default TransmissionsList;
